// Capture data model for the think-aloud corpus (per the brief's Data capture spec).
// All timestamps in the streams are milliseconds relative to the session epoch
// (performance.now origin captured at session start) so traces are ordered without
// depending on wall-clock skew. `startedAt` carries the absolute wall clock.

import { CaptureEnv } from '../lib/env'

export type InteractionEventType =
  | 'mic_on'
  | 'mic_off'
  | 'speech_start'
  | 'speech_end'
  | 'typed_input' // the player edited the candidate input field
  | 'energy_sample' // periodic energy-bar reading
  | 'dim_on' // puzzle dimmed (energy hit threshold)
  | 'dim_off'
  | 'commit'
  | 'reflection_start'
  | 'reflection_end'
  | 'retro_start' // trivial-solve retrospective shown (replaces the passive reflection)
  | 'retro_end' // player finished the retrospective
  | 'answer_detected' // auto-fire credited a spoken solution
  | 'answer_detection_rejected' // player said the detection was wrong
  | 'answer_typed' // player submitted a typed answer
  | 'answer_passed' // player gave up on this puzzle (voluntary)
  | 'timeout' // the 3-minute Wurgaft puzzle limit elapsed (ended unsolved)
  | 'pass_prompt' // player tapped PASS and saw the keep-going confirmation
  | 'restarted' // this puzzle was resumed after being abandoned (discontinuity marker)

export type InteractionEvent = {
  ts: number
  type: InteractionEventType
  // Optional payload, kept small and type-specific:
  energy?: number // for energy_sample
  speaking?: boolean // for energy_sample
  value?: string // for typed_input (current field contents)
}

export type TranscriptItem = {
  ts: number
  text: string
  isFinal: boolean
  confidence: number
}

export type PuzzlePlay = {
  puzzleId: string
  numbers: number[]
  difficulty: string // coarse tier (easy/medium/hard)
  humanRank: number // 4nums human difficulty rank (1 = easiest … 1362 = hardest)
  humanSolveRate: number // % of players who solve it
  // Full interim + final transcript stream with timestamps.
  transcript: TranscriptItem[]
  // Interaction events: typed chars, mic state, energy fluctuations, commit, etc.
  events: InteractionEvent[]
  committedTranscript: string // final speech transcript snapshot at Commit (with pause markers)
  exampleSolution: string | null // a worked solution (shown if the player got it wrong)
  // Auto speech-detection: did we confidently hear a valid solution? If so we credit it
  // and show the detected expression; if not, the player types their answer instead.
  autoFired: boolean
  autoMethod: 'fuzzy' | 'chain' | null // which signal credited it (for calibration analysis)
  autoStars: number // 0–5 confidence (kept for calibration analysis)
  autoSimilarity: number // 0–1 closeness to the best-matching valid solution
  autoMatchedSolution: string | null // the valid expression best matched in speech
  autoRejected: boolean // player said the detection was wrong and typed instead
  // How the puzzle ended: auto-credited from speech, typed in, or passed (gave up).
  answerMode: 'voice-detected' | 'typed' | 'pass' | 'timeout'
  typedAnswer: string | null // raw expression entered on the math keyboard
  typedValue: number | null // what it evaluates to
  typedReason: string | null // correct | wrong-value | wrong-numbers | parse-error | empty
  // Final correctness: voice-detected ⇒ true; typed ⇒ exact judge result; pass ⇒ false.
  // Everything (auto + typed + transcript) is saved for analysis and scorer improvement.
  correct: boolean
  correctnessReason: string // 'voice-detected' | 'typed' | 'pass'
  puzzleStartedAt: string // ISO wall clock
  puzzleDurationMs: number // load → commit
  reflectionDurationMs: number // commit → auto-advance (mic stays open)
}

export type VoiceSession = {
  schemaVersion: number
  sessionId: string // UUID v4, no account
  consented: boolean
  env: CaptureEnv
  startedAt: string // ISO wall clock at session start
  durationMs: number // total session wall-clock duration
  puzzles: PuzzlePlay[]
}

export const SESSION_SCHEMA_VERSION = 1

// ---------------------------------------------------------------------------
// Per-puzzle TRACE — one self-contained Firestore doc per puzzle attempt. This
// supersedes the session-with-puzzles[] model: each trace is written create-only as
// the puzzle finishes (robust to mid-session abandonment), and tagged with everything
// the analyst needs to (a) use it standalone, or (b) reconstruct the strict
// single-sitting Wurgaft-comparable subset. Aborted attempts are kept too (aborted:true)
// with the resumed re-attempt carrying resumed:true + a 'restarted' event.

export type CaptureMode = 'practice' | 'test' | 'play'

export type PuzzleTrace = {
  schemaVersion: number
  // grouping / session
  sessionId: string // UUID v4, no account
  mode: CaptureMode
  conditionId: number | null // full-set arm: Wurgaft condition (0-19); subgoal arm: 21; division arm: 22
  arm: 'full-set' | 'subgoal' | 'division' | null // Phase-2 arm (test mode only); null for practice/play
  trialIndex: number // position within the session (0-based)
  sitting: number // 1 = first visit, 2 = returned after a gap, …
  resumed: boolean // this puzzle attempt began after an abandonment
  aborted: boolean // this attempt was abandoned (partial trace, not completed)
  // puzzle identity + difficulty
  puzzleId: string
  numbers: number[]
  difficulty: string
  decile: number | null // test mode: Wurgaft difficulty band (0 easiest … 9 hardest)
  band: string | null // Phase-2 contrast band: sg-hi / sg-lo / dv-only / dv-free; null otherwise
  humanRank: number // 4nums rank (0 for practice)
  humanSolveRate: number
  // the trace itself
  transcript: TranscriptItem[]
  events: InteractionEvent[]
  committedTranscript: string
  sttEngine: 'webspeech' | 'deepgram' | 'google' // which engine produced `transcript` (for WER analysis)
  // answer + correctness
  exampleSolution: string | null
  autoFired: boolean
  autoMethod: 'fuzzy' | 'chain' | null
  autoStars: number
  autoSimilarity: number
  autoMatchedSolution: string | null
  autoRejected: boolean
  answerMode: 'voice-detected' | 'typed' | 'pass' | 'timeout' | null // null if aborted before answering
  typedAnswer: string | null
  typedValue: number | null
  typedReason: string | null
  correct: boolean
  correctnessReason: string
  // timing
  puzzleStartedAt: string // ISO wall clock
  puzzleDurationMs: number
  reflectionDurationMs: number
  // environment + consent, denormalised so each trace doc stands alone
  env: CaptureEnv
  consented: boolean
  // acquisition attribution (see sourceTag): src route + per-user invite token. Both null
  // for organic sessions. Denormalised onto every trace so a completed-by-source /
  // conversion split needs no session join.
  source: { src: string | null; token: string | null }
  // Was this puzzle unseen on this device before this attempt? Lets the clean per-problem
  // analysis restrict to first exposures (Wurgaft participants each saw every problem once).
  firstExposure: boolean
}

export const TRACE_SCHEMA_VERSION = 6

// ---------------------------------------------------------------------------
// Per-puzzle RETROSPECTIVE ("aha") follow-up — one create-only doc, written only
// when the retro question is administered (a SOLVED test-mode puzzle at trialIndex >=
// the effective N). Absence of a doc for a (sessionId, trialIndex) means "not asked".
// The clean, Wurgaft-comparable concurrent traces are those with trialIndex <= N —
// reconstructable from trialIndex + retroAfterN, so no regime flag lives on PuzzleTrace.

export type PuzzleRetro = {
  schemaVersion: number
  sessionId: string
  trialIndex: number // matches the PuzzleTrace this retro is about
  puzzleId: string
  numbers: number[]
  // Neutral gate (insight vs analytic). null if the player skipped at the gate.
  solveFelt: 'sudden' | 'steady' | null
  // Free-text "what changed?" answer — spoken (mic kept open) and/or typed fallback.
  changedTranscript: TranscriptItem[] | null
  changedText: string | null
  sttEngine: 'webspeech' | 'deepgram' | 'google' // engine that produced changedTranscript
  skipped: boolean // offered but declined (gate or free-text)
  // Provenance so a URL-tampered or misconfigured session is detectable/filterable.
  retroAfterN: number | null // effective N (null = 'off'/Infinity)
  retroCode: string | null // raw ?s= code seen
  promptedAt: string // ISO wall clock when the retro was shown
  answerDurationMs: number
}

export const RETRO_SCHEMA_VERSION = 2
